import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Textarea, Pill } from "../components/UI";
import { BookOpen, RotateCcw, Save, Loader2, CheckCircle2, TrendingUp, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { AiTopicsCard } from "./simple/AiTopicsCard";

export default function PlaybookEditor() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // key -> {tactics, cpm_notes, compliance}
  const [saving, setSaving] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agent/playbooks");
      setRows(data.playbooks || []);
      const d = {};
      (data.playbooks || []).forEach((p) => {
        d[p.key] = {
          tactics: p.override?.tactics || "",
          cpm_notes: p.override?.cpm_notes || "",
          compliance: p.override?.compliance || "",
        };
      });
      setDrafts(d);
    } catch {
      toast.error("Couldn't load playbooks — you may not have admin access.");
    } finally {
      setLoading(false);
    }
    api.get("/agent/playbooks/metrics").then((r) => setMetrics(r.data?.metrics || [])).catch(() => setMetrics([]));
  };
  useEffect(() => { load(); }, []);

  const setDraft = (key, field, val) => {
    setDrafts((d) => ({ ...d, [key]: { ...(d[key] || {}), [field]: val } }));
  };

  const save = async (key) => {
    setSaving(key);
    try {
      await api.put(`/agent/playbooks/${encodeURIComponent(key)}`, drafts[key] || {});
      toast.success(`Saved "${key}" playbook — the AI will use these edits on the next chat turn.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  const reset = async (key) => {
    if (!window.confirm(`Reset "${key}" to the built-in default? Your edits will be discarded.`)) return;
    setResetting(key);
    try {
      await api.delete(`/agent/playbooks/${encodeURIComponent(key)}`);
      toast.success(`"${key}" reset to default`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(null);
    }
  };

  const exportJson = async () => {
    try {
      const { data } = await api.get("/agent/playbooks/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `totalreach-playbooks-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Playbook snapshot downloaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const importFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const overrides = parsed?.overrides || parsed; // accept the raw export shape or a bare overrides map
      const { data } = await api.post("/agent/playbooks/import", { overrides, replace: false });
      const applied = (data.applied || []).length;
      const rejected = (data.rejected || []).length;
      toast.success(`Imported ${applied} override(s)${rejected ? ` · ignored ${rejected} unknown key(s)` : ""}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Import failed — the file must be valid JSON.");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="playbook-editor-page">
      <PageHeader
        eyebrow="Campaign Agent"
        title="Office Playbook Editor"
        subtitle="Tune the tactics, CPM ranges and compliance notes the AI opens with for each office type. Edits apply on the next chat turn — no code deploy required."
      />

      <AiTopicsCard />

      <Card className="!p-4 border-cyan-400/25 bg-cyan-400/5">
        <div className="flex items-start gap-3">
          <BookOpen className="w-4 h-4 text-cyan-300 mt-0.5 shrink-0" />
          <div className="text-xs text-slate-300 leading-relaxed">
            The Campaign Agent injects a playbook into every conversation based on the entity&apos;s <span className="text-cyan-300 font-semibold">office_sought</span> field
            (longest keyword match wins — e.g. &quot;state senate&quot; beats &quot;senate&quot;). Leave a field blank to keep the built-in default.
            Only <span className="text-fuchsia-300 font-semibold">admin</span> and <span className="text-fuchsia-300 font-semibold">super_admin</span> can edit here.
          </div>
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-2" data-testid="playbook-toolbar">
        <Btn variant="secondary" onClick={exportJson} data-testid="playbook-export-btn">
          <Download className="w-4 h-4" /> Export JSON
        </Btn>
        <Btn variant="secondary" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="playbook-import-btn">
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {importing ? "Importing…" : "Import JSON"}
        </Btn>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          data-testid="playbook-import-file"
          onChange={(e) => importFile(e.target.files?.[0])}
        />
        <div className="text-[10px] text-slate-500 ml-1">Bulk edits: export → tweak → import. Unknown keys are ignored.</div>
      </div>

      <PlaybookMetricsCard metrics={metrics} />

      {loading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading playbooks…</div>
      ) : (
        <div className="space-y-4">
          {rows.map((p) => (
            <PlaybookCard
              key={p.key}
              row={p}
              draft={drafts[p.key] || {}}
              onChange={(field, val) => setDraft(p.key, field, val)}
              onSave={() => save(p.key)}
              onReset={() => reset(p.key)}
              saving={saving === p.key}
              resetting={resetting === p.key}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const PlaybookCard = ({ row, draft, onChange, onSave, onReset, saving, resetting }) => {
  const dirty =
    (draft.tactics || "") !== (row.override?.tactics || "") ||
    (draft.cpm_notes || "") !== (row.override?.cpm_notes || "") ||
    (draft.compliance || "") !== (row.override?.compliance || "");

  return (
    <Card className="!p-5" data-testid={`playbook-row-${row.key.replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-display text-lg font-bold text-white capitalize">{row.key}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Office keyword match</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {row.has_override ? (
            <Pill status="Active">Customised{row.updated_by ? ` · ${row.updated_by}` : ""}</Pill>
          ) : (
            <Pill status="Draft">Default</Pill>
          )}
        </div>
      </div>

      <Field
        label="Tactics"
        placeholder={row.default.tactics}
        value={draft.tactics ?? ""}
        onChange={(v) => onChange("tactics", v)}
        testid={`playbook-${row.key.replace(/\s+/g, "-")}-tactics`}
        hint="What tiers / audiences / channels the AI opens with for this race."
      />
      <Field
        label="CPM planning"
        placeholder={row.default.cpm_notes}
        value={draft.cpm_notes ?? ""}
        onChange={(v) => onChange("cpm_notes", v)}
        testid={`playbook-${row.key.replace(/\s+/g, "-")}-cpm`}
        hint="Expected CPMs / spend ranges the AI should quote."
      />
      <Field
        label="Compliance"
        placeholder={row.default.compliance}
        value={draft.compliance ?? ""}
        onChange={(v) => onChange("compliance", v)}
        testid={`playbook-${row.key.replace(/\s+/g, "-")}-compliance`}
        hint="Disclaimer / ethics rules the AI should surface up front."
      />

      <div className="mt-3 flex items-center gap-2 justify-end">
        {row.has_override && (
          <Btn variant="secondary" onClick={onReset} disabled={resetting}
               data-testid={`playbook-reset-${row.key.replace(/\s+/g, "-")}`}>
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Reset to default
          </Btn>
        )}
        <Btn onClick={onSave} disabled={saving || !dirty}
             data-testid={`playbook-save-${row.key.replace(/\s+/g, "-")}`}
             title={dirty ? "Save your edits" : "No changes yet"}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" />
                  : dirty ? <Save className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          {saving ? "Saving…" : dirty ? "Save edits" : "Saved"}
        </Btn>
      </div>
    </Card>
  );
};

const Field = ({ label, value, onChange, placeholder, hint, testid }) => (
  <div className="mb-3">
    <div className="flex items-center justify-between mb-1">
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">{label}</div>
      {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
    </div>
    <Textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={`Default: ${placeholder}`}
      rows={3}
      data-testid={testid}
    />
    <div className="text-[9px] text-slate-600 mt-0.5">Blank = fall back to the built-in default shown above.</div>
  </div>
);


const PlaybookMetricsCard = ({ metrics }) => {
  const rows = (metrics || []).filter((r) => r.sessions > 0);
  if (!rows.length) {
    return (
      <Card className="!p-4" data-testid="playbook-metrics-card">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="w-4 h-4 text-cyan-300" />
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Playbook usage</div>
        </div>
        <div className="text-xs text-slate-500 mt-2">No agent sessions yet — as customers start chatting with the Campaign Agent, this card will show which office playbooks drive the most launched campaigns.</div>
      </Card>
    );
  }
  const totals = rows.reduce(
    (a, r) => ({ sessions: a.sessions + r.sessions, drafts: a.drafts + r.drafts, launched: a.launched + r.launched, budget: a.budget + (r.launched_budget || 0) }),
    { sessions: 0, drafts: 0, launched: 0, budget: 0 },
  );
  return (
    <Card className="!p-4" data-testid="playbook-metrics-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-cyan-300" />
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Playbook usage · which offices actually launch</div>
        </div>
        <div className="text-[10px] text-slate-500">
          {totals.sessions} sessions · {totals.launched} launched · ${Math.round(totals.budget).toLocaleString()} live budget
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="playbook-metrics-table">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500">
            <tr className="border-b border-white/5">
              <th className="text-left py-1.5 pr-3">Office</th>
              <th className="text-right px-2">Sessions</th>
              <th className="text-right px-2">Drafts</th>
              <th className="text-right px-2">Launched</th>
              <th className="text-right px-2">Launch %</th>
              <th className="text-right pl-2">Live budget</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-white/5 last:border-0"
                  data-testid={`playbook-metric-row-${r.key.replace(/\s+/g, "-")}`}>
                <td className="py-1.5 pr-3 text-slate-200 capitalize">
                  {r.key === "__unmatched__" ? <span className="text-slate-500 italic">Unmatched (no office set)</span> : r.key}
                </td>
                <td className="text-right px-2 text-slate-300">{r.sessions}</td>
                <td className="text-right px-2 text-slate-300">{r.drafts}</td>
                <td className={`text-right px-2 font-semibold ${r.launched ? "text-emerald-300" : "text-slate-500"}`}>{r.launched}</td>
                <td className={`text-right px-2 ${r.launch_rate >= 20 ? "text-emerald-300" : r.launch_rate > 0 ? "text-amber-300" : "text-slate-600"}`}>
                  {r.launch_rate}%
                </td>
                <td className="text-right pl-2 text-slate-300">${Math.round(r.launched_budget || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-slate-500 mt-2">
        Session = a Campaign Agent chat opened for this office · Draft = a draft campaign synced · Launched = pushed live to the ad platform.
      </div>
    </Card>
  );
};
